import { useEffect, useMemo, useState } from 'react';
import { X, Send, Mail, MessageSquare, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface Template {
  key: string;
  name: string;
  description: string;
  requires_meeting_url: boolean;
  subject: string;
  body: string;
}

interface Props {
  courseInstanceId: string;
  meetingUrl?: string | null;
  recipientCounts: {
    enrolled: number;
    waitlist: number;
    attendees: number;
    no_shows: number;
  };
  onClose: () => void;
  onSent: () => void;
}

type TargetGroup = 'enrolled' | 'waitlist' | 'attendees' | 'no_shows';
type Channel = 'email' | 'sms';

const TARGET_LABELS: Record<TargetGroup, string> = {
  enrolled: 'Påmeldte',
  waitlist: 'Venteliste',
  attendees: 'Fremmøtte (på dette kurset)',
  no_shows: 'Ikke møtt (avventer ny dato)'
};

export function BroadcastModal({ courseInstanceId, meetingUrl, recipientCounts, onClose, onSent }: Props) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('custom');
  const [targetGroups, setTargetGroups] = useState<TargetGroup[]>(['enrolled']);
  const [channels, setChannels] = useState<Channel[]>(['email', 'sms']);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ recipients_count: number; emails_sent: number; sms_sent: number; emails_failed: number; sms_failed: number } | null>(null);

  // Last maler
  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch('/api/admin/course-instances/broadcast-templates');
        const data = await res.json();
        if (data.success) setTemplates(data.templates);
      } catch (err) {
        console.error('Failed to load broadcast templates', err);
      }
    })();
  }, []);

  // Når mal endres, fyll inn subject/body
  useEffect(() => {
    const tpl = templates.find(t => t.key === selectedTemplate);
    if (tpl && tpl.key !== 'custom') {
      setSubject(tpl.subject);
      setBody(tpl.body);
    } else if (tpl?.key === 'custom') {
      // Behold eksisterende tekst når man bytter til custom
      if (!subject && !body) {
        setSubject('');
        setBody('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, templates]);

  const currentTemplate = templates.find(t => t.key === selectedTemplate);
  const totalRecipients = useMemo(() => {
    return targetGroups.reduce((sum, g) => sum + (recipientCounts[g] || 0), 0);
  }, [targetGroups, recipientCounts]);

  const toggleTarget = (g: TargetGroup) => {
    setTargetGroups(prev =>
      prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]
    );
  };

  const toggleChannel = (c: Channel) => {
    setChannels(prev =>
      prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
    );
  };

  const handleSend = async () => {
    setError(null);
    if (targetGroups.length === 0) { setError('Velg minst én målgruppe'); return; }
    if (channels.length === 0) { setError('Velg minst én kanal (e-post og/eller SMS)'); return; }
    if (!body.trim()) { setError('Skriv en melding'); return; }
    if (channels.includes('email') && !subject.trim()) {
      setError('E-post krever et emne');
      return;
    }
    if (currentTemplate?.requires_meeting_url && !meetingUrl) {
      setError(`Malen "${currentTemplate.name}" krever at møte-URL er satt på kursinstansen (rediger kurs).`);
      return;
    }

    setSending(true);
    try {
      const res = await authenticatedFetch(`/api/admin/course-instances/${courseInstanceId}/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_groups: targetGroups,
          channels,
          template_key: selectedTemplate === 'custom' ? null : selectedTemplate,
          subject: channels.includes('email') ? subject : null,
          message: body
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || data.error || 'Sending feilet');
      } else {
        setResult({
          recipients_count: data.recipients_count,
          emails_sent: data.emails_sent,
          sms_sent: data.sms_sent,
          emails_failed: data.emails_failed,
          sms_failed: data.sms_failed
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sending feilet');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        <div className="border-b px-6 py-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Send melding til kandidater</h2>
            <p className="text-xs text-gray-600 mt-1">SMS og/eller e-post — for kursinformasjon, Teams-lenker, påminnelser.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4 space-y-4 flex-1">
          {result ? (
            <div className="bg-green-50 border border-green-200 rounded p-4 space-y-2">
              <div className="flex items-center gap-2 text-green-900 font-medium">
                <CheckCircle className="h-5 w-5" />
                Melding sendt
              </div>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>Mottakere: {result.recipients_count}</li>
                {channels.includes('email') && (
                  <li>E-poster sendt: {result.emails_sent}{result.emails_failed > 0 ? ` (${result.emails_failed} feilet)` : ''}</li>
                )}
                {channels.includes('sms') && (
                  <li>SMS-er sendt: {result.sms_sent}{result.sms_failed > 0 ? ` (${result.sms_failed} feilet)` : ''}</li>
                )}
              </ul>
            </div>
          ) : (
            <>
              {/* Mal-velger */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mal</label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                >
                  {templates.map(t => (
                    <option key={t.key} value={t.key}>
                      {t.name}{t.requires_meeting_url ? ' (krever møte-URL)' : ''}
                    </option>
                  ))}
                </select>
                {currentTemplate && (
                  <p className="text-xs text-gray-500 mt-1">{currentTemplate.description}</p>
                )}
              </div>

              {/* Målgruppe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mottakere</label>
                <div className="space-y-1.5 bg-gray-50 border rounded p-3">
                  {(['enrolled', 'waitlist', 'attendees', 'no_shows'] as TargetGroup[]).map(g => (
                    <label key={g} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={targetGroups.includes(g)}
                        onChange={() => toggleTarget(g)}
                        disabled={recipientCounts[g] === 0}
                      />
                      <span className={recipientCounts[g] === 0 ? 'text-gray-400' : 'text-gray-800'}>
                        {TARGET_LABELS[g]} <span className="text-gray-500">({recipientCounts[g] || 0})</span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Sender unik melding til {totalRecipients} {totalRecipients === 1 ? 'mottaker' : 'mottakere'} (duplikater fjernes automatisk).
                </p>
              </div>

              {/* Kanal */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kanal</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 text-sm cursor-pointer px-3 py-2 border rounded hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={channels.includes('email')}
                      onChange={() => toggleChannel('email')}
                    />
                    <Mail className="h-4 w-4 text-blue-600" />
                    <span>E-post</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer px-3 py-2 border rounded hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={channels.includes('sms')}
                      onChange={() => toggleChannel('sms')}
                    />
                    <MessageSquare className="h-4 w-4 text-green-600" />
                    <span>SMS</span>
                  </label>
                </div>
              </div>

              {/* Plassholder-hjelp */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                <p className="font-medium mb-1">Tilgjengelige plassholdere:</p>
                <code className="block">{'{navn}'} {'{kursnavn}'} {'{mote_url}'} {'{mote_instruksjoner}'} {'{eksamen_dato}'} {'{kandidatkode}'} {'{arrangor}'}</code>
                {!meetingUrl && (
                  <p className="mt-2 text-amber-800">Tips: For å bruke {'{mote_url}'}, sett møte-URL via "Rediger kurs".</p>
                )}
              </div>

              {/* Emne (kun e-post) */}
              {channels.includes('email') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emne (e-post)</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500"
                    placeholder="F.eks. Praktisk informasjon for {kursnavn}"
                  />
                </div>
              )}

              {/* Meldingstekst */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meldingstekst</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="Skriv melding her — plassholdere som {navn} blir erstattet per mottaker."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tegn: {body.length}{channels.includes('sms') ? ' — SMS forventes <160 tegn ellers blir det flere meldinger' : ''}
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
          {result ? (
            <button
              onClick={() => { onSent(); onClose(); }}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Ferdig
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={sending}
                className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
              >
                Avbryt
              </button>
              <button
                onClick={handleSend}
                disabled={sending || totalRecipients === 0}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? 'Sender...' : `Send til ${totalRecipients}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
